import React from 'react';
import PropTypes from 'prop-types';
import { SnackbarProvider } from 'notistack';
import { FormattedMessage, injectIntl } from 'react-intl';
import BuyerWorkflowControlsContainer from '../../../containers/Succession/BuyerWorkflowControlsContainer';
import DealTeaserSlideshow from './DealTeaserSlideshow';
import BuyerProgressTracker from './BuyerProgressTracker';
import SuccessionDrawerWithListener from '../../../containers/Succession/SuccessionDrawerWithListener';
import FileTab from '../../../containers/FileTabContainer';
import ChatWidget from '../../../containers/ChatWidgetContainer';
import { Provider } from 'react-redux';
import configureStore from '../../../store/findBobPublicStore';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import Alert from '../../../../../ui/canon/AlertCard';
import Badge from '../../../../../ui/canon/Badge';
import EmptyState from '../../../../../ui/canon/EmptyState';
import { CategoryTemplate } from '../../../../../ui/layouts/CategoryTemplate';
import { currencySymbol } from '../../../utils/formatters';
import PartialSaleSourceLine from '../Deals/PartialSaleSourceLine';

// Error boundary component
class BuyerRoomErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("BuyerRoom component error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--succession-bg-secondary)] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.error_boundary.title" />
            </h1>
            <p className="text-[var(--succession-text-secondary)] mb-4">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.error_boundary.description" />
            </p>
            <p className="text-sm text-[var(--succession-text-muted)]">
              <FormattedMessage 
                id="succession_deals.wizard.steps.buyer_room.error_boundary.error_message" 
                values={{ error: this.state.error?.message }}
              />
            </p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="primary"
              size="md"
              className="mt-4"
            >
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.error_boundary.reload_page" />
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

class BuyerRoom extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      justSigned: false
    };
  }

  componentDidMount() {
    console.log("BuyerRoom component rendering with props:", this.props);
    
    // Check for just_signed parameter
    try {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('just_signed') === 'true') {
        this.setState({ justSigned: true });
        // Remove the parameter from URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (error) {
      console.error("Error processing URL parameters:", error);
    }
  }

  handleBackToDeals = () => {
    window.location.href = '/succession/deals';
  }

  handleScheduleMeeting = () => {
    const { deal, dealInterest } = this.props;
    window.location.href = `/deal/${deal.deal_name}/schedule_meeting/${dealInterest.id}`;
  }

  handleMessageNow = () => {
    const { deal } = this.props;
    
    // Try multiple possible paths to get seller username
    let sellerUserName = deal.inventory_user?.user?.fb_user_name || 
                        deal.inventory_user?.user?.user_name ||
                        deal.inventory_user?.user_name ||
                        deal.user?.user_name ||
                        deal.seller?.user_name ||
                        deal.seller?.user?.user_name;
    
    if (!sellerUserName) {
      console.error("No seller username available");
      return;
    }

    console.log(`Opening succession profile drawer for seller: ${sellerUserName} with Messages tab`);

    // Store the tab value (4 is the index for Messages tab) in sessionStorage
    sessionStorage.setItem('profileDrawer_tab', '4');
    
    // Set the username to trigger the succession drawer
    document.x.a = sellerUserName;
  }

  handleDealStructureTool = () => {
    // Open deal structure tool in new tab
    window.open('/cash_flow_analysis', '_blank');
  }

  handleEditCashFlow = (friendlyName) => {
    // Open the specific cash flow analysis in new tab
    window.open(`/calculators/cfa/${friendlyName}`, '_blank');
  }

  formatCurrency = (amount) => {
    const sym = currencySymbol();
    if (!amount) return `${sym}0.00`;
    const cur = (typeof window !== 'undefined' && window.currency) || 'USD';
    const locale = cur === 'GBP' ? 'en-GB' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 2
    }).format(amount);
  }

  getCashFlowType = (cashFlow, intl) => {
    if (cashFlow.buyerHasContinuingPayments) {
      return intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.deal_structure.cash_flow_types.downpayment_percent_earnout' });
    } else if (cashFlow.buyerHasFixedPayments) {
      return intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.deal_structure.cash_flow_types.downpayment_dollar_earnout' });
    } else {
      return intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.deal_structure.cash_flow_types.downpayment_only' });
    }
  }

  renderNegotiatingPhaseActions = () => {
    const { dealInterest, deal, currentUser, company, cashFlows, intl } = this.props;
    
    if (dealInterest.workflow_state !== 'negotiating') {
      return null;
    }

    const sellerName = deal.inventory_user?.user?.name || 'Seller';
    const allowScheduling = company?.feature_types?.includes('allow_scheduling_in_deal_room');

    return (
      <div className="space-y-6 mb-8">
        
        {/* Deal Structure Tool - Cash Flow Analysis */}
        <Card variant="outlined" size="lg">
            <h4 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.title" />
            </h4>
            
            {cashFlows && cashFlows.length > 0 ? (
              <>
                <p className="text-[var(--succession-text-secondary)] mb-6">
                  <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.description" />
                </p>
                
                {/* Cash Flow Scenarios Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[var(--succession-border)]">
                    <thead className="bg-[var(--succession-bg-secondary)]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[var(--succession-text-muted)] uppercase tracking-wider">
                          
                        </th>
                        {cashFlows.map((cashFlow, index) => (
                          <th key={cashFlow.id} className="px-6 py-3 text-center text-xs font-medium text-[var(--succession-text-muted)] uppercase tracking-wider">
                            {cashFlow.name || `Scenario No. ${index + 1}`}
                          </th>
                        ))}
                      </tr>
                      <tr className="bg-[var(--succession-bg-tertiary)]">
                        <td className="px-6 py-2 text-sm font-medium text-[var(--succession-text-primary)]">
                          
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-type`} className="px-6 py-2 text-center text-sm text-[var(--succession-text-secondary)]">
                            {this.getCashFlowType(cashFlow, intl)}
                          </td>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-[var(--succession-bg-primary)] divide-y divide-[var(--succession-border)]">
                      {/* Purchase Price Row */}
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.purchase_price" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-price`} className="px-6 py-4 whitespace-nowrap text-center text-sm text-[var(--succession-text-primary)]">
                            {this.formatCurrency(cashFlow.purchasePrice)}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Down Payment Row */}
                      <tr className="bg-[var(--succession-bg-secondary)]">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.down_payment" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-down`} className="px-6 py-4 whitespace-nowrap text-center text-sm text-[var(--succession-text-primary)]">
                            {this.formatCurrency(cashFlow.buyerLumpSumAmount)}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Financing Row */}
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.financing" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-financing`} className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {cashFlow.buyerHasFinancing ? (
                              <span className="text-green-600">✅</span>
                            ) : (
                              <span className="text-red-600">❌</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Future Fixed Payments Row */}
                      <tr className="bg-[var(--succession-bg-secondary)]">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.future_fixed_payments" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-fixed`} className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {cashFlow.buyerHasFixedPayments ? (
                              <span className="text-green-600">✅</span>
                            ) : (
                              <span className="text-red-600">❌</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Contingent Earn-out Row */}
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.contingent_earnout" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-contingent`} className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {cashFlow.buyerHasContinuingPayments ? (
                              <span className="text-green-600">✅</span>
                            ) : (
                              <span className="text-red-600">❌</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Average 5-yr Cash Flow Row */}
                      <tr className="bg-[var(--succession-bg-tertiary)]">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.avg_5yr_cash_flow" />
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-avg`} className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-[var(--succession-text-primary)]">
                            {this.formatCurrency(cashFlow.buyer1AverageCashFlow)}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Edit Buttons Row */}
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--succession-text-primary)]">
                          
                        </td>
                        {cashFlows.map((cashFlow) => (
                          <td key={`${cashFlow.id}-edit`} className="px-6 py-4 whitespace-nowrap text-center">
                            <Button
                              variant="primary"
                              size="md"
                              onClick={() => this.handleEditCashFlow(cashFlow.friendlyName)}
                            >
                              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.table.edit_button" />
                            </Button>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-[var(--succession-text-secondary)] mb-4">
                <FormattedMessage id="succession_deals.wizard.steps.buyer_room.deal_structure.no_scenarios" />
              </p>
            )}
        </Card>

        {/* Document Sharing Section */}
        <Card variant="outlined" size="lg">
            <h4 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.document_sharing.title" />
            </h4>
            <p className="text-[var(--succession-text-secondary)] mb-4">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.document_sharing.description" />
            </p>
            
            <FileTab
              variables={{ deal_interest_id: dealInterest.id }}
              documents={dealInterest.documents || []}
              disablePopup={true}
              disableUpload={deal.closed}
              dropMessage={intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.document_sharing.drop_message' })}
            />
        </Card>

        {/* Messaging Widget Section */}
        <Card id="message-seller" variant="outlined" size="lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-[var(--succession-text-primary)] flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <FormattedMessage id="succession_deals.wizard.steps.buyer_room.messaging.title" />
                </h4>
                <p className="text-[var(--succession-text-secondary)]">
                  <FormattedMessage 
                    id="succession_deals.wizard.steps.buyer_room.messaging.description" 
                    values={{ sellerName }}
                  />
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="success">
                  <FormattedMessage id="succession_deals.wizard.steps.buyer_room.messaging.status_active" />
                </Badge>
              </div>
            </div>
            
            {dealInterest.inventory_user?.user?.conversation_with_me?.id ? (
              <>
                <div className="border border-[var(--succession-border)] rounded-lg overflow-hidden bg-[var(--succession-bg-secondary)]">
                  <ChatWidget
                    room_id={dealInterest.inventory_user.user.conversation_with_me.id}
                    currentUser={currentUser}
                    title={intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.messaging.chat_title' }, { sellerName })}
                    disableChat={deal.closed}
                    disableChatMessage={intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.messaging.chat_disabled_message' })}
                  />
                </div>

              </>
            ) : (
              <div className="py-8">
                <EmptyState
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  }
                  iconSize="md"
                  title={intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.messaging.conversation_not_available_title' })}
                  description={intl.formatMessage({ id: 'succession_deals.wizard.steps.buyer_room.messaging.conversation_not_available_description' })}
                  action={
                    <Button
                      onClick={this.handleMessageNow}
                      variant="primary"
                      size="md"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <FormattedMessage id="succession_deals.wizard.steps.buyer_room.messaging.start_conversation_button" />
                    </Button>
                  }
                />
              </div>
            )}
        </Card>
      </div>
    );
  }

  render() {
    const { currentUser, company, deal, dealInterest, dealName, loading } = this.props;
    const { justSigned } = this.state;

    // Validation with better error handling
    if (!deal) {
      console.error("BuyerRoom: Missing deal prop");
      return (
        <div className="min-h-screen bg-[var(--succession-bg-secondary)] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.errors.deal_not_found_title" />
            </h1>
            <p className="text-[var(--succession-text-secondary)]">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.errors.deal_not_found_description" />
            </p>
          </div>
        </div>
      );
    }

    if (!dealInterest) {
      console.error("BuyerRoom: Missing dealInterest prop");
      return (
        <div className="min-h-screen bg-[var(--succession-bg-secondary)] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.errors.access_denied_title" />
            </h1>
            <p className="text-[var(--succession-text-secondary)]">
              <FormattedMessage id="succession_deals.wizard.steps.buyer_room.errors.access_denied_description" />
            </p>
          </div>
        </div>
      );
    }

    const workflowState = dealInterest.workflow_state;
    const seller = deal.inventory_user?.user;
    let store = document.store;
    if (!store) {
      store = configureStore({ 
        currentUser: currentUser,
        company: { 
          host: typeof railsContext !== 'undefined' ? railsContext.company_host : window.location.hostname,
          features: typeof railsContext !== 'undefined' ? railsContext.features : [],
          file_import_disclaimer: typeof railsContext !== 'undefined' ? railsContext.file_import_disclaimer : '',
          name: typeof railsContext !== 'undefined' ? railsContext.company_name : company?.name || '',
          branded_name: typeof railsContext !== 'undefined' ? railsContext.company_branded_name : company?.branded_name || '',
          short_name: typeof railsContext !== 'undefined' ? railsContext.company_short_name : company?.short_name || '',
        } 
      });
    }
    return (
      <Provider store={store}>
      <SnackbarProvider>
      <div className="min-h-screen bg-[var(--succession-bg-secondary)]">
        <CategoryTemplate
          variant="wide"
          sidebar={
            <BuyerWorkflowControlsContainer
              dealId={deal.deal_name}
            />
          }
          containerClassName="pt-0"
        >
          {/* Partial Sales: this invite came from a Partial Sale conversation. */}
          <PartialSaleSourceLine partialSaleListing={dealInterest.partial_sale_listing} className="mb-3" />

          {/* Progress Tracker */}
          <BuyerProgressTracker 
            workflowState={workflowState}
            dealInterest={dealInterest}
            deal={deal}
            company={company}
            onBackToDeals={this.handleBackToDeals}
          />

          {/* Just Signed Notification */}
          {justSigned && (
            <Alert 
              variant="success"
              icon={
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              }
              title={<FormattedMessage id="succession_deals.wizard.steps.buyer_room.notifications.nda_signed" />}
            />
          )}

          {/* Deal Teaser */}
          <DealTeaserSlideshow 
            deal={deal}
            company={company}
            currentUser={currentUser}
            dealInterest={dealInterest}
          />

          {/* Negotiating Phase Actions */}
          {this.renderNegotiatingPhaseActions()}
        </CategoryTemplate>

        <SuccessionDrawerWithListener activeDealInterest={dealInterest} />
      </div>
      </SnackbarProvider>
      </Provider>
    );
  }
}

BuyerRoom.propTypes = {
  currentUser: PropTypes.object.isRequired,
  company: PropTypes.object.isRequired,
  deal: PropTypes.object.isRequired,
  dealInterest: PropTypes.object.isRequired,
  dealName: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  cashFlows: PropTypes.array,
  intl: PropTypes.object.isRequired
};

BuyerRoom.defaultProps = {
  loading: false,
  cashFlows: []
};

// Wrap with error boundary and intl
const BuyerRoomWithIntl = injectIntl(BuyerRoom);

const BuyerRoomWithErrorBoundary = (props) => (
  <BuyerRoomErrorBoundary>
    <BuyerRoomWithIntl {...props} />
  </BuyerRoomErrorBoundary>
);

export default BuyerRoomWithErrorBoundary; 